import { ApiError, handleApiError } from '@/handleApiError'
import useAddressValidation from '@/modules/address/useAddressValidation'
import cartApi from '@/modules/cart/api'
import useCartCount from '@/modules/cart/useCartCount'
import helpers from '@/modules/message/helpers'
import api from '@/modules/order/api'
import {
  createOrderPostDto,
  createPreparedOrder,
  createProposedOrderPostDto,
  isOrderAlreadyCreatedError,
  isQuoteAlreadyOrderedError,
  isTimeoutError
} from '@/modules/order/helpers'
import { CHECKOUT_SESSION_TIME_MILLISECONDS, PreparedOrder } from '@/modules/order/types'
import useOrderingConditions from '@/modules/portalSettings/useOrderingConditions'
import quoteApi from '@/modules/quote/api'
import useQuote from '@/modules/quote/useQuote'
import useTopi from '@/modules/topi/useTopi'
import i18n, { t } from '@/plugins/i18n.ts'
import router from '@/router'
import { KeyValue } from '@/types'
import {
  AddressType,
  Cart,
  Checkout,
  ContactData,
  PortalOrderStatus,
  ShopAddress,
  SupplierCart
} from '@/types/index'
import { defineStore } from 'pinia'
import { Ref } from 'vue'

interface CheckoutState {
  checkout: Checkout | undefined
  cart: Cart | SupplierCart | undefined
  preparedOrder: PreparedOrder | undefined
  loading: boolean
  cartLoading: boolean
  sending: boolean
  sendingTopi: boolean
  cartOutdated: boolean
  orderNumberError: string | undefined
  commentsValid: KeyValue[]
  checkoutTerms: string | undefined
  sessionExpired: boolean
  sessionExpiredTimer: ReturnType<typeof setTimeout> | undefined
}

const initialState = (): CheckoutState => {
  return {
    checkout: undefined,
    cart: undefined,
    preparedOrder: undefined,
    loading: false,
    cartLoading: false,
    sending: false,
    sendingTopi: false,
    cartOutdated: false,
    orderNumberError: undefined,
    commentsValid: [],
    checkoutTerms: undefined,
    sessionExpired: false,
    sessionExpiredTimer: undefined
  }
}

const loadCartFinally = (state: CheckoutState) => {
  state.sessionExpired = false
  clearTimeout(state.sessionExpiredTimer)
  useCheckout().cartOutdated = false
  state.cartLoading = false
  state.sessionExpiredTimer = setTimeout(
    () => (state.sessionExpired = true),
    CHECKOUT_SESSION_TIME_MILLISECONDS
  )
}

const useCheckout = defineStore('checkout', {
  state: () => initialState(),
  getters: {
    allCommentsValid(state) {
      return state.commentsValid.every((entry) => {
        return entry.value
      })
    },
    isCommentValid: (state) => {
      return (cartItemId: string) =>
        state.commentsValid.find((entry) => entry.key === cartItemId)?.value
    },
    getCartTimestamp: (state) => {
      return () => state.cart?.timestamp ?? ''
    }
  },
  actions: {
    addCommentValid(id: string, valid: boolean) {
      const entry = this.commentsValid.find((entry) => entry.key === id)
      if (entry) {
        entry.value = valid
      } else {
        this.commentsValid.push({ key: id, value: valid })
      }
    },
    async loadCheckoutTerms() {
      const locale = i18n.global.locale
        ? (i18n.global.locale as unknown as Ref<string>).value
        : navigator.language

      return api
        .loadCheckoutTerms(locale)
        .then(({ data }) => {
          this.checkoutTerms = data
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'FULLPAGE'
          })
        })
    },
    async loadCheckout(supplierCartId?: string) {
      this.sessionExpired = false
      this.loading = true
      this.commentsValid = []
      this.orderNumberError = undefined
      this.loadCheckoutTerms()

      return api
        .loadCheckout(supplierCartId)
        .then(async ({ data }) => {
          this.checkout = data
          this.preparedOrder = createPreparedOrder(data, useOrderingConditions().orderingConditions)

          useAddressValidation().validateAddress(
            this.preparedOrder?.billingAddress,
            AddressType.BILLING
          )
          useAddressValidation().validateAddress(
            this.preparedOrder?.deliveryAddress,
            AddressType.DELIVERY
          )

          await this.loadCart(supplierCartId)
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'FULLPAGE'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async loadCart(supplierCartId?: string, refresh = false) {
      this.cartLoading = true

      if (supplierCartId) {
        return cartApi
          .loadSupplierCart(supplierCartId, true)
          .then(({ data }) => {
            this.cart = data
            useTopi().setCartItemsWithQuantity(data.cartItems)
            if (refresh) {
              helpers.reportSuccess(t('success.cartRefreshed'))
            }
          })
          .catch((error: ApiError) => {
            handleApiError(
              error,
              {
                appearance: 'FULLPAGE'
              },
              { errorCode: 404, logError: false }
            )
          })
          .finally(() => {
            loadCartFinally(this)
          })
      } else {
        return cartApi
          .loadCart(true)
          .then(({ data }) => {
            this.cart = data
            useTopi().setCartItemsWithQuantity(
              data.supplierCarts.flatMap((supplierCart) => supplierCart.cartItems)
            )
            if (refresh) {
              helpers.reportSuccess(t('success.cartRefreshed'))
            }
          })
          .catch((error: ApiError) => {
            handleApiError(error, {
              appearance: 'FULLPAGE'
            })
          })
          .finally(() => {
            loadCartFinally(this)
          })
      }
    },
    async loadCheckoutForQuote(quoteId: string) {
      this.loading = true
      this.commentsValid = []
      this.loadCheckoutTerms()

      return api
        .loadCheckoutForQuote(quoteId)
        .then(async ({ data }) => {
          this.checkout = data
          this.preparedOrder = createPreparedOrder(data, useOrderingConditions().orderingConditions)

          useAddressValidation().validateAddress(
            this.preparedOrder?.billingAddress,
            AddressType.BILLING
          )
          useAddressValidation().validateAddress(
            this.preparedOrder?.deliveryAddress,
            AddressType.DELIVERY
          )

          await useQuote().getQuote(quoteId)

          // If the order was already created (e.g. after a timeout + page reload), redirect
          const orderId = useQuote().quote?.orderId
          if (orderId) {
            await router.push({
              name: 'OrderDetails',
              params: { id: orderId }
            })
          }
        })
        .catch((error: ApiError) => {
          handleApiError(error, {
            appearance: 'FULLPAGE'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async validateOrderNumber(orderNumber: string) {
      return await api
        .validateOrderNumber(orderNumber)
        .then(({ data }) => {
          if (data) {
            this.orderNumberError = undefined
          } else {
            this.orderNumberError = t('order.orderNumberDuplicate', [orderNumber]).toString()
          }
        })
        .catch(handleApiError)
    },
    async sendOrder(supplierCartId?: string) {
      if (this.sending) {
        return
      }
      this.sending = true

      if (this.preparedOrder) {
        return api
          .sendOrder(
            createOrderPostDto(this.preparedOrder, this.getCartTimestamp()),
            supplierCartId
          )
          .then(async ({ data }) => {
            await router.push({
              name: 'OrderDetails',
              params: { id: data.id },
              query: { orderStatus: PortalOrderStatus.SENT }
            })
          })
          .catch((error: ApiError) => {
            if (error.code == 409) {
              this.cartOutdated = true
            } else {
              handleApiError(error, undefined, {
                errorCode: 403,
                logError: false
              })
            }
          })
          .finally(() => {
            useCartCount().loadCartCount()
            this.sending = false
          })
      }
    },
    async sendOrderTopi(supplierCartId?: string) {
      if (this.sendingTopi) {
        return
      }
      if (this.preparedOrder) {
        this.sendingTopi = true
        return api
          .sendOrderTopi(
            createOrderPostDto(this.preparedOrder, this.getCartTimestamp()),
            supplierCartId
          )
          .then(({ data }) => {
            if (data) {
              window.location.href = data
            }
          })
          .catch((error: ApiError) => {
            if (error.code == 409) {
              this.cartOutdated = true
            } else {
              handleApiError(error, undefined, {
                errorCode: 403,
                logError: false
              })
            }
            this.sendingTopi = false
          })
          .finally(() => {
            useCartCount().loadCartCount()
          })
      }
    },
    async sendQuoteOrder(quoteId: string, timestamp: string) {
      if (this.sending) {
        return
      }

      this.sending = true

      if (!this.preparedOrder) {
        this.sending = false
        return
      }

      const orderPostDTO = createOrderPostDto(this.preparedOrder, timestamp)
      orderPostDTO.timestamp = useQuote().getVersion()

      return api
        .sendQuoteOrder(orderPostDTO, quoteId)
        .then(({ data }) => {
          router.push({
            name: 'OrderDetails',
            params: { id: data.id },
            query: { orderStatus: PortalOrderStatus.SENT }
          })
        })
        .catch(async (error: ApiError) => {
          if (
            isTimeoutError(error) ||
            (error.code == 409 && isOrderAlreadyCreatedError(error)) ||
            isQuoteAlreadyOrderedError(error)
          ) {
            try {
              const { data } = await quoteApi.getQuote(quoteId)
              if (data.orderId) {
                await router.push({
                  name: 'OrderDetails',
                  params: { id: data.orderId }
                })
                return
              }
            } catch (quoteError: unknown) {
              // 403 = quote already ordered, but orderId unavailable → redirect to orders list
              if ((quoteError as ApiError).code === 403) {
                await router.push({ name: 'Orders' })
                return
              }
            }
            if (isTimeoutError(error)) {
              helpers.reportRefreshError(t('order.orderTimeoutError'))
            } else {
              helpers.reportWarning(t('order.orderAlreadyBeingProcessed'))
            }
          } else if (error.code == 409) {
            this.cartOutdated = true
          } else {
            handleApiError(error, undefined, {
              errorCode: 403,
              logError: false
            })
          }
          this.sending = false
        })
    },
    async requestOrder(commentToApprovers: string, supplierCartId?: string) {
      if (this.sending) {
        return
      }
      if (!this.preparedOrder) {
        if (!this.checkout) {
          return
        }
        this.preparedOrder = createPreparedOrder(
          this.checkout,
          useOrderingConditions().orderingConditions
        )
      }
      this.sending = true

      return api
        .requestOrder(
          createProposedOrderPostDto(
            this.preparedOrder,
            this.getCartTimestamp(),
            commentToApprovers
          ),
          supplierCartId
        )
        .then(({ data }) => {
          helpers.reportSuccess(t('order.orderRequested'))
          router.push({
            name: 'OrderDetails',
            params: { id: data.id }
          })
        })
        .catch((error: ApiError) => {
          if (error.code == 409) {
            this.cartOutdated = true
          } else {
            handleApiError(error)
          }
        })
        .finally(() => {
          useCartCount().loadCartCount()
          this.sending = false
        })
    },
    async requestQuoteOrder(commentToApprovers: string, quoteId: string, timestamp: string) {
      if (this.sending) {
        return
      }
      if (!this.preparedOrder) {
        if (!this.checkout) {
          return
        }
        this.preparedOrder = createPreparedOrder(
          this.checkout,
          useOrderingConditions().orderingConditions
        )
      }
      this.sending = true

      return api
        .requestQuoteOrder(
          createProposedOrderPostDto(this.preparedOrder, timestamp, commentToApprovers),
          quoteId
        )
        .then(({ data }) => {
          helpers.reportSuccess(t('order.orderRequested'))
          router.push({
            name: 'OrderDetails',
            params: { id: data.id }
          })
        })
        .catch((error: ApiError) => {
          if (error.code == 409) {
            this.cartOutdated = true
          } else {
            handleApiError(error)
          }
        })
        .finally(() => {
          this.sending = false
        })
    },
    setPartialShipmentSelection(selection: boolean) {
      if (this.preparedOrder) {
        this.preparedOrder.usePartialShipment = selection
      }
    },
    setOrderNumber(orderNumber: string) {
      if (this.preparedOrder) {
        this.preparedOrder.orderNumber = orderNumber
      }
    },
    setCustomerOrderId(customerOrderId: string) {
      if (this.preparedOrder) {
        this.preparedOrder.customerOrderId = customerOrderId
      }
    },
    setOrderComment(comment: string) {
      if (this.preparedOrder) {
        this.preparedOrder.orderComment = comment
      }
    },
    setBillingAddress(address: ShopAddress) {
      if (this.preparedOrder) {
        this.preparedOrder.billingAddress = address
        useAddressValidation().validateAddress(address, AddressType.BILLING)
      }
    },
    setDeliveryAddress(address: ShopAddress) {
      if (this.preparedOrder) {
        this.preparedOrder.deliveryAddress = address
        useAddressValidation().validateAddress(address, AddressType.DELIVERY)
      }
    },
    setBillingContactData(contactData?: ContactData) {
      if (this.preparedOrder) {
        this.preparedOrder.billingContactData = contactData
      }
    },
    setDeliveryContactData(contactData?: ContactData) {
      if (this.preparedOrder) {
        this.preparedOrder.deliveryContactData = contactData
      }
    }
  }
})

export default useCheckout
